/**
 * Workflow Engine — Public API
 *
 * Central initialization and exports for the event bus,
 * workflow engine, notification router, and polling service.
 */

// Core types
export * from './types';

// EventBus
export { eventBus } from './eventBus';

// TriggerMatcher
export { triggerMatcher, evaluateCondition } from './triggerMatcher';

// WorkflowEngine
export { executeWorkflow, registerStepExecutor, getRegisteredExecutorTypes } from './workflowEngine';

// WorkflowRegistry
export { workflowRegistry } from './workflowRegistry';

// NotificationRouter
export { notificationRouter } from './notificationRouter';
export type { NotificationPushFn, NotificationEnabledCheckFn, DndCheckFn } from './notificationRouter';

// PollingService
export { pollingService, registerPollingHandler } from './pollingService';
export type { PolledItem } from './pollingService';

// Polling Handlers
export { registerAllPollingHandlers, setupDefaultPollingSources, createEmailPollingSource } from './pollingHandlers';

// UserActionEmitter
export { emitFromToolResult } from './userActionEmitter';

// Step executors
export { registerAllStepExecutors } from './steps';
export { registerCustomHandler, unregisterCustomHandler, getCustomHandlerNames } from './steps/customStep';
export type { EmailClassification, EmailClassificationResult } from './steps/emailClassifierStep';
export type { ClientMatchResult } from './steps/clientMatcherStep';

// Notification actions
export {
  registerNotificationAction,
  unregisterNotificationAction,
  executeNotificationAction,
  getRegisteredActions,
} from './notificationActions';
export type { NotificationActionHandler } from './notificationActions';

// Pre-built workflows
export {
  emailClassificationWorkflow,
  installEmailClassificationWorkflow,
  uninstallEmailClassificationWorkflow,
  testEmailClassificationWorkflow,
  testWorkEmailOnly,
  testCustomEmail,
} from './workflows';

// Test utilities
export { testWorkflowEngine, testEventBus, testTriggerMatcher, testWorkflowExecution, testNotificationRouter, testWorkflowRegistry } from './test';

// Mutation Interceptor (automatic event emission from GraphQL mutations)
export {
  initMutationInterceptor,
  configureMutationInterceptor,
  disableMutationInterceptor,
  enableMutationInterceptor,
  processMutation,
} from '../../database/mutationInterceptor';

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

import { registerAllStepExecutors } from './steps';
import { workflowRegistry } from './workflowRegistry';
import { triggerMatcher } from './triggerMatcher';
import { executeWorkflow } from './workflowEngine';
import { notificationRouter } from './notificationRouter';
import { pollingService } from './pollingService';
import { eventBus } from './eventBus';
import { registerMutationInterceptor, unregisterMutationInterceptor } from '../../database/graphqlClient';
import { initMutationInterceptor, processMutation } from '../../database/mutationInterceptor';
import { registerAllPollingHandlers, setupDefaultPollingSources } from './pollingHandlers';
import { installEmailClassificationWorkflow } from './workflows';
import type { NotificationPushFn, NotificationEnabledCheckFn, DndCheckFn } from './notificationRouter';
import type { InAppNotification } from './types';

export interface InitWorkflowEngineOptions {
  /** Function to push an in-app notification to the store */
  pushNotification: NotificationPushFn;
  /** Function to check if a notification type is enabled for inApp */
  isEnabledForInApp?: NotificationEnabledCheckFn;
  /** Function to check DnD status */
  isInDnD?: DndCheckFn;
  /** Current user ID */
  currentUserId?: string;
  /** Whether to start the polling service (email, messages, etc.) */
  enablePolling?: boolean;
  /** Whether to install the email classification workflow */
  enableEmailClassification?: boolean;
}

/**
 * Initialize the complete workflow engine system.
 *
 * Call this once at application startup (e.g. in App.tsx or main.ts).
 *
 * 1. Registers all step executors
 * 2. Registers all polling handlers
 * 3. Loads saved workflows from persistence
 * 4. Hooks into GraphQL mutations (automatic event emission)
 * 5. Starts TriggerMatcher (on match → execute workflow)
 * 6. Starts NotificationRouter (on event → push notification)
 * 7. Optionally starts PollingService (email, messages)
 * 8. Optionally installs email classification workflow
 */
export function initWorkflowEngine(options: InitWorkflowEngineOptions): void {
  // 1. Register all step executors
  registerAllStepExecutors();

  // 2. Register all polling handlers (email, messages, etc.)
  registerAllPollingHandlers();

  // 3. Load saved workflows
  workflowRegistry.load();

  // 4. Hook into GraphQL mutations → emit events to EventBus
  // This is the "funnel" - ALL database changes go through here
  initMutationInterceptor((event) => eventBus.emit(event), {
    debug: false,
    ignoreMutations: [
      'saveNotificationSettings',
      'saveUserPreferences',
      'updateLastSeen',
      'markNotificationRead',
      'saveWorkflow', // Avoid infinite loops when saving workflows
    ],
  });

  // Register the interceptor with graphqlMutation
  registerMutationInterceptor((mutation, variables, result) => {
    processMutation(mutation, variables, result, options.currentUserId);
  });

  // 5. Start TriggerMatcher → execute workflows on match
  triggerMatcher.start((workflow, event) => {
    executeWorkflow(workflow, event).catch((err) => {
      console.error(`[WorkflowEngine] Execution failed for "${workflow.name}":`, err);
    });
  });

  // 6. Start NotificationRouter
  notificationRouter.start({
    pushNotification: options.pushNotification,
    isEnabledForInApp: options.isEnabledForInApp,
    isInDnD: options.isInDnD,
    currentUserId: options.currentUserId,
  });

  // 7. Start PollingService if enabled (email, messages, etc.)
  if (options.enablePolling) {
    setupDefaultPollingSources();
    pollingService.start();
    console.log('[WorkflowEngine] Polling service started (email every 30s)');
  }

  // 8. Install email classification workflow if enabled
  if (options.enableEmailClassification !== false) {
    installEmailClassificationWorkflow();
    console.log('[WorkflowEngine] Email classification workflow installed');
  }

  console.log('[WorkflowEngine] Initialized successfully');
}

/**
 * Shut down the workflow engine cleanly
 */
export function shutdownWorkflowEngine(): void {
  // Unhook from GraphQL mutations
  unregisterMutationInterceptor();

  // Stop all services
  triggerMatcher.stop();
  notificationRouter.stop();
  pollingService.stop();

  console.log('[WorkflowEngine] Shut down');
}
